import { createSupabaseClient } from "@zitiu/supabase-client";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

type Client = ReturnType<typeof createSupabaseClient>;

let client: Client | null = null;

/**
 * Build on first use, not at module scope.
 *
 * `createSupabaseClient` throws when VITE_SUPABASE_URL or
 * VITE_SUPABASE_PUBLISHABLE_KEY is missing. Calling it during module
 * evaluation meant that throw aborted the module, leaving `export const
 * supabase` permanently in its temporal dead zone. Every importer that touched
 * it afterwards reported
 *
 *   Cannot access 'supabase' before initialization
 *
 * which names neither the missing variable nor the module that failed, and
 * reads exactly like a circular import — there isn't one. The real error
 * ("VITE_SUPABASE_URL is required") was raised once, during evaluation, and
 * then lost behind the TDZ message at every call site.
 *
 * Deferring means the first actual use raises the real error, at a real call
 * site, and says which variable is missing.
 */
function getClient(): Client {
  if (!client) {
    client = createSupabaseClient({
      url: import.meta.env.VITE_SUPABASE_URL,
      anonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    });
  }
  return client;
}

/**
 * A stand-in that builds the client on first property access.
 *
 * Methods are bound to the real client: without that, `supabase.from(...)`
 * would run with `this` set to the proxy and fail inside supabase-js.
 */
export const supabase: Client = new Proxy({} as Client, {
  get(_target, prop) {
    const real = getClient() as unknown as Record<string | symbol, unknown>;
    const value = real[prop];
    return typeof value === "function" ? value.bind(real) : value;
  },
  has(_target, prop) {
    return prop in (getClient() as unknown as object);
  },
});

/** Get Authorization header with the user's session JWT for edge function calls. */
export async function getAuthHeader(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("No active session");
  return `Bearer ${session.access_token}`;
}
